import { useEffect, useState } from "react";
import {
  useResourcePersonBooks,
  useResourcePersonCourses,
  useResourcePersonLessons,
  useResourcePersonLearningPaths,
} from "../store/resourcePerson/resourcePersonStore";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { BookOpen, TrendingUp, Star, Layers, Plus, Pen, Send, Eye, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { useNavigate } from "react-router-dom";
import { Badge } from "../components/ui/badge";
import courseWebDev from "../assets/course-web-dev.jpg";
import courseMobile from "../assets/course-mobile.jpg";
import courseDataScience from "../assets/course-data-science.jpg";
import courseDesign from "../assets/course-design.jpg";
import courseProgramming from "../assets/course-programming.jpg";
import RetryBanner from "../components/resourcePerson/RetryBanner";
import { imageUrl } from "../lib/format";
import {Skeleton} from "../components/ui/skeleton";

const PLACEHOLDER_IMAGES = [
  courseWebDev,
  courseMobile,
  courseDataScience,
  courseDesign,
  courseProgramming,
];

/** Status pill colours for the Stage 1→3 lifecycle. */
const statusStyles = {
  PENDING: "bg-amber-50 text-amber-700",
  APPROVED: "bg-emerald-50 text-emerald-700",
  REJECTED: "bg-rose-50 text-rose-700",
  DISABLED: "bg-slate-100 text-slate-600",
};

export default function ContentLibrary() {
  const [view, setView] = useState("courses");
  const navigate = useNavigate();
  const { fetchBooks } = useResourcePersonBooks();
  const { courses, coursesLoading, coursesError, fetchCourses } = useResourcePersonCourses();
  const { fetchModuleCount } = useResourcePersonLessons();
  const {
    learningPaths,
    learningPathsLoading,
    learningPathsError,
    fetchLearningPaths,
  } = useResourcePersonLearningPaths();

  // Per-course module counts for the library cards. Prefer the count the list
  // API already returns (moduleCount/modules); only fetch modules for courses
  // that don't carry one, so we stay accurate with minimal extra requests.
  const [moduleCounts, setModuleCounts] = useState({});
  const [navigatingCourseId, setNavigatingCourseId] = useState(null);

  const openCourse = (courseId, page = "build", action = page) => {
    setNavigatingCourseId(`${courseId}:${action}`);
    navigate(`/resource-person/content-library/course/${courseId}/${page}`);
  };

  useEffect(() => {
    fetchBooks({ page: 1, limit: 10, sort: "newest" });
  }, [fetchBooks]);

  // GET /api/resource-person/courses — the lecturer's own drafts + published work.
  useEffect(() => {
    fetchCourses({ page: 1, limit: 50, sort: "newest" });
  }, [fetchCourses]);

  // GET /api/resource-person/learning-paths — the lecturer's bundled paths.
  useEffect(() => {
    fetchLearningPaths({ page: 1, limit: 50, sort: "newest" });
  }, [fetchLearningPaths]);

  // Fill in module counts once the list arrives. A course that already exposes
  // a module count is recorded immediately; otherwise we fetch its modules.
  // Requests run sequentially so the shared `modules` store state is never
  // clobbered by overlapping calls.
  useEffect(() => {
    const list = Array.isArray(courses) ? courses : [];
    if (!list.length) return;

    let cancelled = false;
    const provided = {};
    const needsFetch = [];

    list.forEach((course) => {
      if (!course?.id) return;
      const known =
        course.moduleCount ??
        course.modulesCount ??
        (Array.isArray(course.modules) ? course.modules.length : undefined) ??
        (Array.isArray(course.module_lessons) ? course.module_lessons.length : undefined);
      if (known !== undefined) provided[course.id] = known;
      else needsFetch.push(course.id);
    });

    if (Object.keys(provided).length) {
      setModuleCounts((prev) => ({ ...prev, ...provided }));
    }

    (async () => {
      for (const courseId of needsFetch) {
        if (cancelled) return;
        // fetchModuleCount is read-only, so the open builder's module list is
        // never overwritten while we count cards here.
        const result = await fetchModuleCount(courseId);
        if (cancelled) return;
        if (result?.success) setModuleCounts((prev) => ({ ...prev, [courseId]: result.count }));
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [courses, fetchModuleCount]);

  const courseList = Array.isArray(courses) ? courses : [];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-4xl font-extrabold tracking-tight mb-2">Content Library</h1>
          <p className="text-muted-foreground text-lg">
            Manage all your courses and learning materials
          </p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3">
        <Button
          size="lg"
          className={`font-medium border px-4 py-2 rounded transition-all duration-300
            ${view === "courses"
              ? "bg-blue-600 text-white border-blue-600 hover:bg-blue-700"
              : "bg-white text-gray-700 border-gray-400 hover:bg-gray-100 hover:text-gray-900"
            }`}
          onClick={() => setView("courses")}
        >
          <BookOpen className="h-4 w-4 mr-2" />
          List of Courses
        </Button>

        <Button
          size="lg"
          className={`font-medium border px-4 py-2 rounded transition-all duration-300
            ${view === "create"
              ? "bg-green-600 text-white border-green-600 hover:bg-green-700"
              : "bg-white text-gray-700 border-gray-400 hover:bg-gray-100 hover:text-gray-900"
            }`}
          onClick={() => setView("create")}
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Content
        </Button>

        <Button
          size="lg"
          className={`font-medium border px-4 py-2 rounded transition-all duration-300
            ${view === "paths"
              ? "bg-purple-600 text-white border-purple-600 hover:bg-purple-700"
              : "bg-white text-gray-700 border-gray-400 hover:bg-gray-100 hover:text-gray-900"
            }`}
          onClick={() => setView("paths")}
        >
          <Layers className="h-4 w-4 mr-2" />
          Learning Paths
        </Button>
      </div>

      {/* Conditional Sections */}
      {view === "courses" && (
        <div className="space-y-4">
          {coursesLoading && courseList.length === 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
              {[0, 1, 2].map((index) => (
                <Card key={index} aria-label="Loading course" className="overflow-hidden border-0 shadow-lg">
                  <Skeleton className="h-48 w-full rounded-none" />
                  <CardContent className="space-y-4 p-6">
                    <div className="space-y-2">
                      <Skeleton className="h-5 w-3/4" />
                      <Skeleton className="h-4 w-1/2" />
                    </div>
                    <div className="flex justify-between border-t pt-4">
                      <Skeleton className="h-4 w-20" />
                      <Skeleton className="h-4 w-24" />
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Skeleton className="h-9 flex-1" />
                      <Skeleton className="h-9 w-10" />
                      <Skeleton className="h-9 flex-1" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : null}

          {coursesError ? (
            <RetryBanner
              message={coursesError}
              className="mt-6 mb-0"
              onRetry={() => fetchCourses({page: 1, limit: 50, sort: "newest"})}
            />
          ) : null}

          {!coursesLoading && courseList.length === 0 && !coursesError ? (
            <Card className="mt-6 border-2 border-dashed border-slate-200 bg-white p-10 text-center shadow-none">
              <BookOpen className="mx-auto mb-3 h-12 w-12 text-slate-300" />
              <h3 className="text-xl font-bold text-slate-800">No courses yet</h3>
              <p className="mx-auto mt-2 max-w-md text-slate-500">
                Create a draft, add at least one lesson, then submit it for review.
              </p>
              <Button
                onClick={() => navigate("/resource-person/content-library/course/new")}
                className="mt-6 rounded bg-blue-900 px-6 py-2 font-medium text-white hover:bg-blue-800"
              >
                <Plus className="h-4 w-4 mr-2" /> Create your first course
              </Button>
            </Card>
          ) : null}

          {courseList.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-6">
              {courseList.map((course, index) => {
                const status = String(course.status || "PENDING").toUpperCase();
                const cover = course.coverImage || course.cover_image;
                return (
                  <Card key={course.id} className="border-0 shadow-lg overflow-hidden group transition-shadow duration-300">
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={cover ? imageUrl(cover) : PLACEHOLDER_IMAGES[index % PLACEHOLDER_IMAGES.length]}
                        alt={course.title}
                        className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      />
                      <div className="absolute top-3 right-3">
                        <Badge className={`font-semibold ${statusStyles[status] || statusStyles.PENDING}`}>{status}</Badge>
                      </div>
                    </div>

                    <CardContent className="p-6 space-y-4">
                      <div>
                        <h3 className="text-lg font-semibold mb-1 line-clamp-2">{course.title}</h3>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <BookOpen className="h-4 w-4" />
                          <span>{course.category?.name || course.categoryName || "Uncategorised"}</span>
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-3 border-t">
                        <div className="flex items-center gap-2">
                          <Layers className="h-4 w-4 text-info" />
                          <span className="text-sm">
                            {course.moduleCount ?? course.modulesCount ?? moduleCounts[course.id] ?? (
                              <Skeleton className="inline-block h-4 w-12 align-middle" aria-label="Loading module count" />
                            )} modules
                          </span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Star className="h-4 w-4 text-warning fill-warning" />
                          <span className="text-sm">
                            {course.enrollmentCount ?? course.students ?? 0} students
                          </span>
                        </div>
                      </div>

                      <div className="flex gap-2 pt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          disabled={navigatingCourseId === `${course.id}:build`}
                          onClick={() => openCourse(course.id)}
                        >
                          {navigatingCourseId === `${course.id}:build`
                            ? <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                            : <Pen className="mr-1 h-4 w-4" />}
                          {status === "DRAFT" ? "Build" : status === "PENDING" ? "Review" : "View"}
                        </Button>
                        {/* Read-only preview: shows the course as a learner will see
                            it, including whether every video/document actually
                            resolves. Icon-only (title carries the meaning) so the
                            card's three actions stay readable at 3-up. */}
                        <Button
                          variant="outline"
                          size="sm"
                          className="px-2"
                          title="Preview what learners will see"
                          aria-label="Preview what learners will see"
                          disabled={navigatingCourseId === `${course.id}:preview`}
                          onClick={() => openCourse(course.id, "preview")}
                        >
                          {navigatingCourseId === `${course.id}:preview`
                            ? <Loader2 className="h-4 w-4 animate-spin" />
                            : <Eye className="h-4 w-4" />}
                        </Button>
                        <Button
                          size="sm"
                          className="flex-1 gradient-primary text-white hover:brightness-90"
                          disabled={navigatingCourseId === `${course.id}:submit`}
                          onClick={() => openCourse(course.id, "build", "submit")}
                        >
                          {navigatingCourseId === `${course.id}:submit`
                            ? <Loader2 className="mr-1 h-4 w-4 animate-spin" />
                            : <Send className="mr-1 h-4 w-4" />}
                          Submit
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : null}
        </div>
      )}

      {view === "create" && (
        <Card className="border border-gray-300 rounded-lg p-10 text-center shadow-lg bg-white">
          <h2 className="text-2xl font-semibold mb-3 text-gray-800">Create New Course Content</h2>
          <p className="text-gray-600">
            Build a course draft, add lessons and quiz questions, then submit it for review.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Button
              onClick={() => navigate("/resource-person/content-library/course/new")}
              className="px-6 py-2 rounded bg-green-600 text-white font-medium transition-colors duration-300 hover:bg-green-700"
            >
              <Plus className="h-4 w-4 mr-2" /> New course
            </Button>
            <Button
              onClick={() => navigate("/resource-person/content-library/path/new")}
              variant="outline"
              className="px-6 py-2 rounded font-medium"
            >
              <Layers className="h-4 w-4 mr-2" /> New learning path
            </Button>
          </div>
          <p className="mx-auto mt-4 max-w-lg text-xs text-gray-500">
            A course must belong to a category. You can create a category on the spot inside the
            builder — it becomes usable immediately and fills the course&apos;s <code>categoryId</code>.
          </p>
        </Card>
      )}

      {view === "paths" && (
        <Card className="border-0 shadow-lg overflow-hidden mt-6">
          <CardHeader className="bg-gradient-to-r from-blue-500/10 via-blue-100/10 to-transparent pb-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="p-3 bg-blue-500 rounded-2xl shadow-lg">
                <TrendingUp className="h-7 w-7 text-white" />
              </div>
              <div>
                <CardTitle className="text-2xl font-bold">Learning Paths</CardTitle>
                <p className="text-muted-foreground mt-1 text-base">
                  Bundle your own draft courses with lessons into an ordered path
                </p>
              </div>
            </div>
          </CardHeader>
          <CardContent className="pt-8 pb-8">
            {learningPathsError ? (
              <RetryBanner
                message={learningPathsError}
                className="mb-4"
                onRetry={() => fetchLearningPaths({ page: 1, limit: 50, sort: "newest" })}
              />
            ) : null}

            {learningPathsLoading && (!learningPaths || learningPaths.length === 0) ? (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {[0, 1, 2].map((index) => (
                  <Skeleton key={index} className="h-40 w-full" />
                ))}
              </div>
            ) : !learningPaths || learningPaths.length === 0 ? (
              <div className="text-center py-12 bg-muted/30 rounded-2xl border-2 border-dashed">
                <TrendingUp className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" />
                <h3 className="text-xl font-bold mb-2">Build a learning path</h3>
                <p className="text-muted-foreground max-w-md mx-auto">
                  Add draft courses that each have at least one lesson, reorder them, then submit the
                  path for admin review.
                </p>
                <Button
                  onClick={() => navigate("/resource-person/content-library/path/new")}
                  className="mt-6 rounded bg-blue-900 px-6 py-2 font-medium text-white hover:bg-blue-800"
                >
                  <Plus className="h-4 w-4 mr-2" /> New learning path
                </Button>
              </div>
            ) : (
              <>
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                  <p className="m-0 text-sm text-slate-500">
                    {learningPaths.length} learning path{learningPaths.length === 1 ? "" : "s"}
                  </p>
                  <Button
                    onClick={() => navigate("/resource-person/content-library/path/new")}
                    className="rounded bg-blue-900 px-5 py-2 font-medium text-white hover:bg-blue-800"
                  >
                    <Plus className="h-4 w-4 mr-2" /> New learning path
                  </Button>
                </div>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {learningPaths.map((path) => {
                    const status = String(path.status || "DRAFT").toUpperCase();
                    return (
                      <Card key={path.id} className="border border-slate-200 shadow-sm">
                        <CardContent className="space-y-3 p-5">
                          <div className="flex items-start justify-between gap-2">
                            <h3 className="m-0 line-clamp-2 text-base font-semibold text-slate-900">
                              {path.title}
                            </h3>
                            <Badge className={`shrink-0 font-semibold ${statusStyles[status] || "bg-slate-100 text-slate-600"}`}>
                              {status}
                            </Badge>
                          </div>
                          {path.description ? (
                            <p className="m-0 line-clamp-2 text-sm text-slate-500">{path.description}</p>
                          ) : null}
                          <div className="flex items-center justify-between border-t border-slate-100 pt-3 text-sm text-slate-500">
                            <span className="flex items-center gap-1">
                              <BookOpen className="h-4 w-4" />
                              {Array.isArray(path.courses) ? path.courses.length : 0} courses
                            </span>
                            <span className="font-semibold text-slate-800">₦{Number(path.price || 0).toLocaleString()}</span>
                          </div>
                          <div className="flex gap-2 pt-1">
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1"
                              onClick={() => navigate(`/resource-person/content-library/path/${path.id}/build`)}
                            >
                              <Pen className="h-4 w-4 mr-1" /> Edit
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              className="flex-1"
                              onClick={() => navigate(`/resource-person/content-library/path/${path.id}`)}
                            >
                              <Eye className="h-4 w-4 mr-1" /> View
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}